export const addCountryBoundariesLayer = (map, countryFilterCodes) => {
    map.addLayer(
        {
            id: 'country-boundaries',
            source: {
                type: 'vector',
                url: 'mapbox://mapbox.country-boundaries-v1',
            },
            'source-layer': 'country_boundaries',
            type: 'fill',
            paint: {
                'fill-color': '#E9F4FF',
                'fill-outline-color': '#0C42FF',
                'fill-opacity': 0.4,
            },
        },
        'country-label'
    );

    map.setFilter('country-boundaries', [
        'in',
        'iso_3166_1',
        ...countryFilterCodes,
    ]);
};

export const addPointsSource = (map, mapPois) => {
    map.addSource('points', {
        type: 'geojson',
        data: mapPois,
        cluster: true,
        clusterMaxZoom: 50,
        clusterRadius: 60,
        clusterProperties: {
            hasActive: ['any', ['boolean', ['get', 'isActive'], false]],
        },
    });
};

// The glyph every holdings marker carries. Named here rather than at each call
// site, so the marker on the map and a legend swatch naming it can never drift
// to different files.
export const HOLDINGS_ICON_URL = '/img/suitcase.svg';

// One conceptual holdings layer on the map, four mapbox layers underneath
const HOLDINGS_LAYER_IDS = [
    'clusters',
    'cluster-count',
    'unclustered-point-circle',
    'unclustered-point',
];

const toVisibility = (isShown) => (isShown ? 'visible' : 'none');

export const setHoldingsLayersVisibility = (map, isShown) => {
    const visibility = toVisibility(isShown);
    HOLDINGS_LAYER_IDS.forEach((id) => {
        // The icon layer arrives async with its image; skip what is not there yet
        if (map.getLayer(id)) {
            map.setLayoutProperty(id, 'visibility', visibility);
        }
    });
};

export const addClusterLayers = (map, isShown = true) => {
    const visibility = toVisibility(isShown);

    map.addLayer({
        id: 'clusters',
        type: 'circle',
        source: 'points',
        filter: ['has', 'point_count'],
        layout: { visibility },
        paint: {
            'circle-color': [
                'case',
                ['==', ['get', 'hasActive'], true],
                '#D2E8FF', // highlight färg
                '#fff',
            ],
            'circle-stroke-color': '#ACD5FF',
            'circle-stroke-width': 1.5,
            'circle-radius': ['step', ['get', 'point_count'], 18, 100, 21],
        },
    });

    map.addLayer({
        id: 'cluster-count',
        type: 'symbol',
        source: 'points',
        filter: ['has', 'point_count'],
        layout: {
            visibility,
            'text-field': '{point_count_abbreviated}',
            'text-font': ['Source Sans Pro SemiBold', 'DIN Offc Pro Medium'],
            'text-size': 14,
            'text-font-weight': 'bold',
        },
        paint: {
            'text-color': '#3D96FF',
        },
    });
};

export const addUnclusteredPointLayers = (
    map,
    activeHolding,
    isShown = true
) => {
    const visibility = toVisibility(isShown);

    map.addLayer({
        id: 'unclustered-point-circle',
        type: 'circle',
        source: 'points',
        filter: ['!', ['has', 'point_count']],
        layout: { visibility },
        paint: {
            'circle-color': [
                'case',
                ['==', ['get', 'sourceId'], activeHolding],
                '#0C2632',
                '#ffffff',
            ],
            'circle-stroke-color': [
                'case',
                ['==', ['get', 'sourceId'], activeHolding],
                '#0C2632',
                '#ACD5FF',
            ],
            'circle-stroke-width': 1.5,
            'circle-radius': 16,
        },
    });

    const img = new Image(90, 90);
    img.onload = () => {
        map.addImage('icon', img);
        map.addLayer({
            id: 'unclustered-point',
            type: 'symbol',
            source: 'points',
            filter: ['!', ['has', 'point_count']],
            layout: {
                // The image decode can outlive a visibility toggle, which
                // skips layers not yet added — so follow the sibling layers
                visibility:
                    map.getLayoutProperty?.('clusters', 'visibility') ??
                    visibility,
                'icon-image': 'icon',
                'icon-size': 0.2,
            },
        });
    };
    img.src = HOLDINGS_ICON_URL;
};

export const addWorldBoundsDebugLayer = (map, worldBounds) => {
    if (!worldBounds) return;

    const cornersGeoJSON = getWorldBoundsCornersGeoJSON(worldBounds);
    map.addSource('world-bounds-corners', {
        type: 'geojson',
        data: cornersGeoJSON,
    });
    map.addLayer({
        id: 'world-bounds-corners-layer',
        type: 'circle',
        source: 'world-bounds-corners',
        paint: {
            'circle-radius': 7,
            'circle-color': '#000000',
            'circle-stroke-width': 1.5,
            'circle-stroke-color': '#ffffff',
        },
    });
};
